import { useCallback, useMemo } from "react";
import { useSearchParams } from "react-router-dom";

/**
 * Guarda búsqueda, orden, filtros y página en la URL.
 *
 * Para que una vista filtrada se pueda pegar en un chat —«mira estas
 * llamadas»— y para que recargar no tire por tierra el trabajo de filtrar.
 *
 * Cada tabla lleva su prefijo porque dos tablas en la misma ruta se pisarían
 * los parámetros, y el resultado sería una tabla cambiando sola al tocar la
 * otra.
 *
 * Lo que **no** se guarda: la selección de filas y las columnas ocultas.
 * Compartir un enlace que trae cosas seleccionadas invita a actuar sobre lo
 * que otro eligió.
 */
export type TableUrlState = {
  search: string;
  setSearch: (value: string) => void;
  sort: { id: string; desc: boolean } | null;
  setSort: (value: { id: string; desc: boolean } | null) => void;
  filters: Record<string, string>;
  setFilter: (columnId: string, value: string | undefined) => void;
  page: number;
  setPage: (value: number) => void;
  clear: () => void;
};

export function useTableUrlState(key: string | undefined): TableUrlState | null {
  const [params, setParams] = useSearchParams();

  const prefijo = key ? `${key}_` : "";
  const nombre = useCallback((campo: string) => `${prefijo}${campo}`, [prefijo]);

  const escribir = useCallback(
    (cambios: Record<string, string | undefined>) => {
      setParams(
        (previos) => {
          const siguientes = new URLSearchParams(previos);
          for (const [campo, valor] of Object.entries(cambios)) {
            if (valor === undefined || valor === "") siguientes.delete(campo);
            else siguientes.set(campo, valor);
          }
          return siguientes;
        },
        // `replace` y no `push`: cada letra tecleada en el buscador sería una
        // entrada del historial, y volver atrás tendría que deshacer la
        // búsqueda carácter a carácter.
        { replace: true },
      );
    },
    [setParams],
  );

  return useMemo(() => {
    if (!key) return null;

    const orden = params.get(nombre("sort"));
    const filtros: Record<string, string> = {};
    for (const [clave, valor] of params.entries()) {
      if (clave.startsWith(`${prefijo}f_`)) filtros[clave.slice(prefijo.length + 2)] = valor;
    }

    return {
      search: params.get(nombre("q")) ?? "",
      setSearch: (value) => escribir({ [nombre("q")]: value, [nombre("page")]: undefined }),
      sort: orden
        ? { id: orden.replace(/^-/, ""), desc: orden.startsWith("-") }
        : null,
      setSort: (value) =>
        escribir({ [nombre("sort")]: value ? `${value.desc ? "-" : ""}${value.id}` : undefined }),
      filters: filtros,
      setFilter: (columnId, value) =>
        // Filtrar devuelve a la primera página: quedarse en la cinco de un
        // resultado que ahora tiene dos deja la tabla en blanco sin motivo
        // aparente.
        escribir({ [`${prefijo}f_${columnId}`]: value, [nombre("page")]: undefined }),
      page: Math.max(0, Number(params.get(nombre("page")) ?? "0") || 0),
      setPage: (value) => escribir({ [nombre("page")]: value === 0 ? undefined : String(value) }),
      clear: () =>
        escribir({
          [nombre("q")]: undefined,
          [nombre("sort")]: undefined,
          [nombre("page")]: undefined,
          ...Object.fromEntries(
            Object.keys(filtros).map((columnId) => [`${prefijo}f_${columnId}`, undefined]),
          ),
        }),
    };
  }, [key, params, prefijo, nombre, escribir]);
}
